import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';

const KIND = {
  CA_ACCOUNTANT: 'CA or accounting firm',
  TALLY_SOFTWARE_PARTNER: 'Tally or software partner',
  IT_CONSULTANT: 'IT or ERP consultant',
  OTHER: 'Something else'
};
const STATUS_BADGE = { NEW: 'warning', APPROVED: 'success', REJECTED: 'secondary' };

/** System Admin > Account Manager Applications: people who applied on /partners. Approving makes the account manager record. */
export default function PartnerApplications() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('NEW');
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/system-admin/partner-applications').then((r) => { setData(r.data); setFailed(false); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body, done) => {
    setBusyId(id);
    try {
      await axios.post(`/api/system-admin/partner-applications/${id}/${path}`, body);
      toast.success(done);
      setRejecting(null);
      setNote('');
      load();
    } catch (e) {
      toast.error(typeof e.response?.data === 'string' ? e.response.data : 'Could not save that');
    } finally {
      setBusyId(null);
    }
  };

  if (failed) return <Alert variant="danger" className="mt-3">Could not load the applications.</Alert>;
  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const rows = data.applications.filter((a) => filter === 'ALL' || a.status === filter);
  return (
    <div className="mt-3" data-testid="partner-applications">
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <h5 className="mb-0">Account manager applications</h5>
        <Badge bg={data.waiting > 0 ? 'warning' : 'secondary'} text={data.waiting > 0 ? 'dark' : undefined}>{data.waiting} waiting</Badge>
        <Form.Select size="sm" style={{ width: 'auto' }} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show">
          <option value="NEW">Waiting</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Not taken forward</option>
          <option value="ALL">All</option>
        </Form.Select>
      </div>
      <p className="text-muted small">
        From the form on <a href="/partners" target="_blank" rel="noopener noreferrer">ibigdo.com/partners</a>. Approving makes the account manager record
        (under Account Managers) and nothing else: agree the terms with them, then assign their first customer, which is when their login is made.
      </p>
      <Table responsive hover size="sm" className="align-middle">
        <thead><tr><th>Applied</th><th>Who</th><th>Describes themself as</th><th>Works with</th><th>Came from</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td className="text-nowrap">{formatDateTime(a.createdAt)}</td>
              <td>
                <div className="fw-semibold">{a.name}{a.firm ? `, ${a.firm}` : ''}</div>
                <div className="small"><a href={`mailto:${a.email}`}>{a.email}</a> &middot; {a.phone}{a.city ? ` · ${a.city}` : ''}</div>
                {a.message && <div className="small text-muted fst-italic">&ldquo;{a.message}&rdquo;</div>}
              </td>
              <td>{KIND[a.kind] || a.kind}</td>
              <td>{a.clients || ''}</td>
              <td className="small text-muted">{a.source || ''}</td>
              <td>
                <Badge bg={STATUS_BADGE[a.status]} text={a.status === 'NEW' ? 'dark' : undefined}>{a.status === 'NEW' ? 'Waiting' : a.status === 'APPROVED' ? 'Approved' : 'Not taken forward'}</Badge>
                {a.reviewedBy && <div className="small text-muted">{a.reviewedBy}</div>}
                {a.reviewNote && <div className="small text-muted">{a.reviewNote}</div>}
              </td>
              <td className="text-nowrap text-end">
                {a.status === 'NEW' && (
                  <>
                    <Button size="sm" variant="success" className="me-1" disabled={busyId === a.id} onClick={() => act(a.id, 'approve', {}, 'Account manager record made')}>Approve</Button>
                    <Button size="sm" variant="outline-secondary" disabled={busyId === a.id} onClick={() => setRejecting(a)}>Not now</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No applications here.</td></tr>}
        </tbody>
      </Table>

      <Modal show={!!rejecting} onHide={() => setRejecting(null)}>
        <Modal.Header closeButton><Modal.Title>Not taking {rejecting?.name} forward</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>A note for yourself (optional, the applicant is not told)</Form.Label>
            <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejecting(null)}>Cancel</Button>
          <Button variant="primary" onClick={() => act(rejecting.id, 'reject', { note }, 'Marked as not taken forward')}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
